// @flow
import './index.less';

import {EllipsisOutlined} from '@ant-design/icons';

import {Dropdown, Menu, message} from 'antd';
import classNames from 'classnames';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import React, {useCallback, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import BasicSpinner from '_components/BasicSpinner';
import {useAuth} from '_contexts/auth';
import iconCheckCircle from '_images/icon-check-circle-fill.png';
import getCardLogo from '_utils/getCardLogo';
import {getStripeCustomer} from '_utils/profile';
import {detachPaymentMethod, setDefaultPaymentMethod} from '_utils/stripe';

type Props = {
  paymentMethod: Object,
  isDefault?: boolean,
  editable?: boolean,
};

const PaymentMethod = ({
  paymentMethod,
  isDefault = false,
  editable = false,
  selected = false,
  selectable = false,
  onSelect = () => {},
}: Props): React$Node => {
  const {t} = useTranslation();

  const {updateProfile: updateProfileContext} = useAuth();

  const logo = useMemo(() => getCardLogo(paymentMethod.card.brand), [paymentMethod]);
  const exp = useMemo(
    () => ({
      month: `${paymentMethod.card.expMonth || paymentMethod.card.exp_month}`.padStart(2, '0'),
      year: paymentMethod.card.expYear || paymentMethod.card.exp_year,
    }),
    [paymentMethod],
  );

  const [processing, setProcessing] = useState(false);

  const handleDelete = useCallback(() => {
    (async () => {
      setProcessing(true);
      try {
        await detachPaymentMethod(uuidv4(), paymentMethod.id);
        const stripeCustomer = await getStripeCustomer();
        updateProfileContext({stripeCustomer});
      } catch (err) {
        let errMsg = 'Failed to delete payment method';
        if (err.message) {
          errMsg = err.message;
          if (err.message.includes('in use')) {
            errMsg = t('PaymentMethod_CannotDeleteInUseErrorMessage');
          }
        }
        message.error(errMsg);
      } finally {
        setProcessing(false);
      }
    })();
  }, [paymentMethod, updateProfileContext]);

  const handleSetDefault = useCallback(() => {
    (async () => {
      setProcessing(true);
      try {
        await setDefaultPaymentMethod(uuidv4(), paymentMethod.id);
        const stripeCustomer = await getStripeCustomer();
        updateProfileContext({stripeCustomer});
      } catch (err) {
        message.error('Failed to set default payment method');
      } finally {
        setProcessing(false);
      }
    })();
  }, [paymentMethod, updateProfileContext]);

  const handleSelect = useCallback(() => {
    if (!selectable) return;
    onSelect(paymentMethod);
  }, [paymentMethod, selectable]);

  const actionMenuElem = useMemo(
    () => (
      <Menu className="rf-dropdown-menu">
        <Menu.Item className="rf-dropdown-menu-item" onClick={handleDelete}>
          {t('Common_Delete')}
        </Menu.Item>
        {!isDefault && (
          <Menu.Item className="rf-dropdown-menu-item" onClick={handleSetDefault}>
            {t('Common_SetAsDefault')}
          </Menu.Item>
        )}
      </Menu>
    ),
    [t, isDefault, handleDelete, handleSetDefault],
  );

  return (
    <div className={classNames('payment-method', {selected, selectable})} onClick={handleSelect}>
      <img className="card-logo" src={logo} alt="Card Logo" />
      <div className="card-info">
        <span className="masked-card-num">
**** -
{paymentMethod.card.last4}
        </span>
        <span className="expire">
          exp 
{' '}
{exp.month} / 
{' '}
{exp.year}
        </span>
        {isDefault && <span className="label">{t('Common_Primary')}</span>}
        {selected && <img className="selected-icon" src={iconCheckCircle} alt="" />}
      </div>
      {editable && (
        <BasicSpinner loading={processing}>
          <Dropdown overlay={actionMenuElem} trigger={['click']}>
            <a
              className="actions-dropdown-btn"
              onClick={e => e.preventDefault()}
              onKeyPress={null}
              role="button"
              tabIndex="0">
              <EllipsisOutlined />
            </a>
          </Dropdown>
        </BasicSpinner>
      )}
    </div>
  );
};

export default PaymentMethod;
